import { useMutation } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useUpdateConversationParticipant from "common/datahooks/useUpdateConversationParticipant";
import useUpdateDetailedLead from "common/datahooks/useUpdateDetailedLead";
import useUpdateLead from "common/datahooks/useUpdateLead";
import { del, post } from "common/helpers/HTTP";
import type { Lead } from "common/types";

async function addFavoriteRequest(workspaceId: string, leadId: string) {
  await post(`workspaces/${workspaceId}/leads/${leadId}/favorite`);
}

async function removeFavoriteRequest(workspaceId: string, leadId: string) {
  await del(`workspaces/${workspaceId}/leads/${leadId}/favorite`);
}

export default function useFavorite() {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { updateLead } = useUpdateLead();
  const { updateDetailedLead } = useUpdateDetailedLead();
  const { updateConversationParticipant } = useUpdateConversationParticipant();

  async function updateCachedLead(leadId: string, isFavorite: boolean) {
    function updateFunction(lead: Pick<Lead, "favorite">) {
      lead.favorite = isFavorite;
    }
    await updateLead(leadId, updateFunction);
    await updateDetailedLead(leadId, updateFunction);
    await updateConversationParticipant(leadId, updateFunction);
  }

  const { mutateAsync: addFavorite } = useMutation({
    mutationFn: ({ leadId }: { leadId: string }) =>
      addFavoriteRequest(workspaceId, leadId),
    onMutate: async ({ leadId }) => {
      await updateCachedLead(leadId, true);
    },
    onError: async (_, { leadId }) => {
      await updateCachedLead(leadId, false);
    },
  });

  const { mutateAsync: removeFavorite } = useMutation({
    mutationFn: ({ leadId }: { leadId: string }) =>
      removeFavoriteRequest(workspaceId, leadId),
    onMutate: async ({ leadId }) => {
      await updateCachedLead(leadId, false);
    },
    onError: async (_, { leadId }) => {
      await updateCachedLead(leadId, true);
    },
  });

  return {
    addFavorite,
    removeFavorite,
  };
}
